import React, {
  createContext,
  useContext,
  useMemo,
  useEffect,
  useState,
  useCallback,
} from 'react';
import { useEntityPermission } from '../../providers/EntityPermissionProvider';
import FormErrorsArray from './FormErrorsArray';
import FormLoading from './FormLoading';
import FormProviderRenderer from './FormProviderRenderer';
import { useFormDebug } from './FormDebugProvider';

const FormPermissionContext = createContext({});

/**
 * FormPermissionProvider is a React context provider that manages form-level permissions.
 * It fetches permissions for specified entities using the useEntityPermission hook,
 * handles loading and error states, and provides the permissions object to its children.
 *
 * Props:
 * - permissionEntities (Array<string> | null): List of entity names to fetch permissions for.
 * - permittedByDefault (Boolean): allow all field write access.
 * - children (ReactNode): Child components that require access to form permissions.
 *
 * Usage:
 * Wrap your form components with FormPermissionProvider and pass the required entity names.
 * Use the useFormPermissions hook inside child components to access permissions.
 */
export function FormPermissionProvider({
  permissionEntities = null,
  permittedByDefault = false,
  staticPermissions = null,
  children,
}) {
  const debug = useFormDebug();
  const { getPermission } = useEntityPermission();
  const [state, setState] = useState({
    permissions: null,
    errors: [],
    loading: true,
  });

  // stabilize entity list
  const entitiesKey = useMemo(
    () =>
      Array.isArray(permissionEntities) ? permissionEntities.join('|') : '',
    [permissionEntities],
  );

  const loadPermissions = useCallback(async () => {
    if (staticPermissions !== null) {
      setState({ permissions: staticPermissions, errors: [], loading: false });
      return;
    }

    if (permissionEntities === null) {
      setState({ permissions: null, errors: [], loading: false });
      return;
    }

    if (!Array.isArray(permissionEntities)) {
      setState({
        permissions: null,
        errors: [{ message: 'Permission entities should be an array' }],
        loading: false,
      });
      return;
    }

    try {
      const results = await Promise.all(
        permissionEntities.map(entity => getPermission({ name: entity })),
      );

      const errors = [];
      const permissions = {};

      permissionEntities.forEach((entity, idx) => {
        if (!results[idx]) {
          errors.push({
            message: `Error while getting permissions for entity: ${entity}`,
          });
        } else {
          Object.assign(permissions, results[idx]);
        }
      });

      setState({
        permissions,
        errors,
        loading: false,
      });
    } catch (err) {
      setState({
        permissions: null,
        errors: [{ message: err?.message }],
        loading: false,
      });
    }
  }, [entitiesKey, getPermission]);

  useEffect(() => {
    debug.addStep('FormPermission');
    loadPermissions();
  }, []);

  const value = useMemo(
    () => ({
      permissions: state.permissions,
      permittedByDefault,
      staticPermissions,
    }),
    [state.permissions, permittedByDefault, staticPermissions],
  );

  if (state.loading) {
    return <FormLoading>Fetching Form Permissions</FormLoading>;
  }

  if (Array.isArray(state?.errors)) {
    if (state.errors.length > 0) {
      return <FormErrorsArray errors={state?.errors} />;
    }
  } else if (state?.errors) {
    return (
      <FormErrorsArray errors={[{ message: JSON.stringify(state?.errors) }]} />
    );
  }

  return (
    <FormPermissionContext.Provider value={value}>
      <FormProviderRenderer
        message="Loading Permissions"
        loading={state.loading}
        errors={state?.errors}>
        {children}
      </FormProviderRenderer>
    </FormPermissionContext.Provider>
  );
}

export const useFormPermissions = () => {
  return useContext(FormPermissionContext);
};
